import { useCallback, useEffect, useMemo, useState } from 'react';
import { adminRequest, AdminApiError } from './admin-api';
import {
  type AchievementLanguage,
  type AchievementSort,
  type AdminAchievementItem,
  type AdminAchievementOverview,
  earnedShare,
  rewardSummary,
  ruleSummary,
  sortAchievements,
  summarizeAchievements,
} from './achievements-overview-model';

type LoadState =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'ready'; overview: AdminAchievementOverview };

const languages: ReadonlyArray<{ id: AchievementLanguage; label: string }> = [
  { id: 'en', label: 'EN' },
  { id: 'sk', label: 'SK' },
  { id: 'cs', label: 'CS' },
];
const sorts: ReadonlyArray<{ id: AchievementSort; label: string }> = [
  { id: 'order', label: 'App order' },
  { id: 'most', label: 'Most earned' },
  { id: 'least', label: 'Least earned' },
];

export function AchievementsOverview({
  getAccessToken,
  onAccessDenied,
}: {
  getAccessToken(): Promise<string>;
  onAccessDenied(): void;
}) {
  const [state, setState] = useState<LoadState>({ kind: 'loading' });
  const [language, setLanguage] = useState<AchievementLanguage>('en');
  const [sort, setSort] = useState<AchievementSort>('order');

  const load = useCallback(async () => {
    setState({ kind: 'loading' });
    try {
      const overview = (await adminRequest(
        getAccessToken,
        '/admin/achievements',
      )) as AdminAchievementOverview;
      setState({ kind: 'ready', overview });
    } catch (cause) {
      if (cause instanceof AdminApiError && (cause.status === 401 || cause.status === 403)) {
        onAccessDenied();
        return;
      }
      setState({
        kind: 'error',
        message: 'Achievements could not be loaded. Please try again.',
      });
    }
  }, [getAccessToken, onAccessDenied]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <section className="min-w-0 space-y-6" aria-busy={state.kind === 'loading'}>
      <header className="admin-page-header flex flex-wrap items-start justify-between gap-5">
        <div className="max-w-2xl">
          <p className="admin-eyebrow">Reader journey</p>
          <h1>Achievements</h1>
          <p>
            Read-only overview of every achievement and how many readers have earned it.
            Achievements are defined in code, so changes ship with a release.
          </p>
        </div>
        <button
          className="admin-button shrink-0"
          disabled={state.kind === 'loading'}
          onClick={() => void load()}
          type="button"
        >
          Refresh
        </button>
      </header>

      {state.kind === 'loading' ? (
        <p className="admin-empty" role="status">
          Loading achievements…
        </p>
      ) : state.kind === 'error' ? (
        <div className="admin-empty" role="alert">
          <p className="m-0">{state.message}</p>
          <button className="admin-button mt-4" onClick={() => void load()} type="button">
            Try again
          </button>
        </div>
      ) : (
        <AchievementsOverviewContent
          language={language}
          onLanguageChange={setLanguage}
          onSortChange={setSort}
          overview={state.overview}
          sort={sort}
        />
      )}
    </section>
  );
}

/** Pure view of a loaded overview, kept separate from data loading for render tests. */
export function AchievementsOverviewContent({
  language,
  onLanguageChange,
  onSortChange,
  overview,
  sort,
}: {
  language: AchievementLanguage;
  onLanguageChange(language: AchievementLanguage): void;
  onSortChange(sort: AchievementSort): void;
  overview: AdminAchievementOverview;
  sort: AchievementSort;
}) {
  const items = useMemo(() => sortAchievements(overview.items, sort), [overview, sort]);
  return (
    <>
      <SummaryTiles overview={overview} />
      <DriftNotice items={overview.items} />
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="admin-tabs" role="group" aria-label="Sort achievements">
          {sorts.map(({ id, label }) => (
            <button
              aria-pressed={sort === id}
              key={id}
              onClick={() => onSortChange(id)}
              type="button"
            >
              {label}
            </button>
          ))}
        </div>
        <div className="admin-tabs" role="group" aria-label="Copy language">
          {languages.map(({ id, label }) => (
            <button
              aria-pressed={language === id}
              key={id}
              onClick={() => onLanguageChange(id)}
              type="button"
            >
              {label}
            </button>
          ))}
        </div>
      </div>
      <AchievementTable items={items} language={language} readers={overview.readers} />
      <p className="m-0 text-xs text-ink-muted">
        Shares are of all {overview.readers} registered readers. Updated{' '}
        {formatTimestamp(overview.generatedAt)}.
      </p>
    </>
  );
}

function SummaryTiles({ overview }: { overview: AdminAchievementOverview }) {
  const summary = summarizeAchievements(overview);
  const tiles = [
    ['Readers', summary.readers],
    ['Live achievements', `${summary.live} / ${summary.total}`],
    ['Unlocks', summary.unlocks],
    ['Unlocks, last 7 days', summary.unlocksLast7Days],
    ['Needs attention', summary.needsAttention],
  ] as const;
  return (
    <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-5">
      {tiles.map(([label, value]) => (
        <div
          className="rounded-[10px] border border-line/35 bg-canvas-deep/45 px-4 py-4"
          key={label}
        >
          <span className="block text-xs font-semibold uppercase tracking-[0.08em] text-ink-muted">
            {label}
          </span>
          <strong
            className={`mt-2 block font-serif text-2xl font-medium ${
              label === 'Needs attention' && summary.needsAttention > 0
                ? 'text-warning'
                : 'text-ink'
            }`}
          >
            {value}
          </strong>
        </div>
      ))}
    </div>
  );
}

function DriftNotice({ items }: { items: readonly AdminAchievementItem[] }) {
  const flagged = items.filter((item) => item.drift.length > 0);
  if (!flagged.length) return null;
  return (
    <section
      className="rounded-xl border border-danger/25 bg-danger-fill/40 p-4 text-sm leading-6 text-danger"
      role="alert"
    >
      <p className="m-0 font-semibold">
        Code and database disagree for {flagged.length} achievement(s)
      </p>
      <ul className="mb-0 mt-2 list-disc pl-5">
        {flagged.map((item) => (
          <li key={item.id}>
            <code>{item.id}</code>: {item.drift.join(' ')}
          </li>
        ))}
      </ul>
    </section>
  );
}

function AchievementTable({
  items,
  language,
  readers,
}: {
  items: readonly AdminAchievementItem[];
  language: AchievementLanguage;
  readers: number;
}) {
  if (!items.length) {
    return <p className="admin-empty">No achievements are defined.</p>;
  }
  return (
    <section className="overflow-hidden rounded-[17px] border border-line/75 bg-panel/80 shadow-panel">
      <div className="overflow-x-auto">
        <table className="w-full min-w-[980px] border-collapse text-left text-sm">
          <thead className="bg-canvas-deep/55 text-xs uppercase tracking-[0.08em] text-ink-muted">
            <tr>
              <th className="px-5 py-3 font-semibold sm:px-6">Achievement</th>
              <th className="px-5 py-3 font-semibold">Unlocks when</th>
              <th className="px-5 py-3 font-semibold">Reward</th>
              <th className="px-5 py-3 font-semibold">Status</th>
              <th className="px-5 py-3 font-semibold">Earned by</th>
              <th className="px-5 py-3 font-semibold">Last 7 days</th>
              <th className="px-5 py-3 font-semibold">Last unlocked</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => {
              const share = earnedShare(item.unlocks.total, readers);
              return (
                <tr className="border-t border-line/25 align-top" key={item.id}>
                  <td className="max-w-[340px] px-5 py-4 sm:px-6">
                    <strong className="block text-ink">
                      {item.title?.[language] ?? 'Not shown in the app'}
                    </strong>
                    <code className="mt-1 block text-xs text-ink-muted">{item.id}</code>
                    {item.description ? (
                      <span className="mt-2 block text-xs leading-5 text-ink-soft">
                        {item.description[language]}
                      </span>
                    ) : null}
                    {item.groupTitle ? (
                      <span className="mt-2 block text-[11px] font-semibold uppercase tracking-[0.08em] text-ink-muted">
                        {item.groupTitle}
                      </span>
                    ) : null}
                  </td>
                  <td className="px-5 py-4 text-ink-soft">{ruleSummary(item)}</td>
                  <td className="px-5 py-4 text-ink-soft">
                    {rewardSummary(item.reward)}
                    {item.reward.collectibleId ? (
                      <code className="mt-1 block text-xs text-ink-muted">
                        {item.reward.collectibleId}
                      </code>
                    ) : null}
                  </td>
                  <td className="px-5 py-4">
                    <AchievementStatus item={item} />
                  </td>
                  <td className="px-5 py-4">
                    <span className="font-semibold text-ink">{item.unlocks.total}</span>
                    <span className="text-ink-muted"> · {share}%</span>
                    <span
                      aria-hidden="true"
                      className="mt-2 block h-1.5 w-28 overflow-hidden rounded-full bg-canvas-deep"
                    >
                      <span
                        className="block h-full rounded-full bg-thread"
                        style={{ width: `${Math.min(100, share)}%` }}
                      />
                    </span>
                  </td>
                  <td className="px-5 py-4 text-ink-soft">{item.unlocks.last7Days}</td>
                  <td className="whitespace-nowrap px-5 py-4 text-ink-muted">
                    {formatTimestamp(item.unlocks.lastAt)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function AchievementStatus({ item }: { item: AdminAchievementItem }) {
  const [label, classes] = item.drift.length
    ? ['Check', 'border-danger/45 bg-danger-fill/45 text-warning']
    : item.enabled
      ? ['Live', 'border-safe/35 bg-safe-fill text-safe']
      : ['Disabled', 'border-line/45 bg-canvas-deep/55 text-ink-muted'];
  return (
    <span className={`inline-flex rounded-full border px-3 py-1 text-xs font-bold ${classes}`}>
      {label}
    </span>
  );
}

function formatTimestamp(value: string | null) {
  if (!value) return 'Never';
  return new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' }).format(
    new Date(value),
  );
}
